import React, { useState, useMemo } from "react";
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ClipboardDocumentCheckIcon,
  ArrowPathIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

// --- GLOBAL MOCK DATA ---
const MOCK_COMPLIANCE_METRICS = {
    complianceRate: 92,
    policyViolations: 18,
    pendingApprovals: 5,
    onTimeCheckIns: 145,
};

const MOCK_ATTENDANCE_EXCEPTIONS = [
    { id: 301, agent: "Priya Sharma", type: "Late Check-In", details: "10:15 AM (15 min late)", gpsProof: true, status: "Pending", reason: "Traffic detour due to accident." },
    { id: 302, agent: "Vikram Kumar", type: "Early Check-Out", details: "04:30 PM (30 min early)", gpsProof: false, status: "Pending", reason: "Urgent personal appointment." },
    { id: 303, agent: "Ravi Menon", type: "Geo-fence Breach", details: "Entered Restricted Area X", gpsProof: true, status: "Approved", reason: "Needed to use public restroom." },
];

const MOCK_VIOLATION_LOGS = [
    { id: 401, time: "10:15 AM", agent: "Arjun Singh", type: "Speeding Alert", location: "Highway Stretch, Pune Road", severity: "High" },
    { id: 402, time: "11:30 AM", agent: "Priya Sharma", type: "Geo-fence Breach", location: "Industrial Zone (Restricted)", severity: "Critical" },
    { id: 403, time: "09:05 AM", agent: "Vikram Kumar", type: "Route Deviation", location: "Missed mandatory stop 1", severity: "Medium" },
    { id: 404, time: "12:00 PM", agent: "Ravi Menon", type: "Idle Time", location: "Near client site B", severity: "Low" },
];

// --- HELPER COMPONENTS ---

// Summary Card Component (Style refinement)
const ComplianceSummaryCard = ({ title, value, icon: Icon, colorClass, footer }) => (
    // Enhanced hover effect
    <div className="bg-white p-5 rounded-xl shadow-lg border border-gray-200 flex flex-col justify-between h-full transition duration-300 hover:shadow-xl hover:border-indigo-300">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm font-poppins font-medium text-gray-500">{title}</p>
                <p className="text-3xl font-poppins font-bold text-gray-900 mt-2">{value}</p>
            </div>
            <div className={`p-3 rounded-full ${colorClass}/10`}>
                <Icon className={`h-7 w-7 ${colorClass}`} />
            </div>
        </div>
        <p className="text-xs font-roboto mt-4 text-gray-400 border-t pt-2">{footer}</p>
    </div>
);

// Action Button with Loading State
const ActionButton = ({ onClick, children, className, loading }) => (
    <button
        onClick={onClick}
        disabled={loading}
        // Added focus and active states for enterprise polish
        className={`px-4 py-2 text-sm font-semibold rounded-lg transition duration-150 shadow-md flex items-center justify-center 
            focus:outline-none focus:ring-2 focus:ring-offset-2 ${loading ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
    >
        {loading ? (
            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
        ) : (
            children
        )}
    </button>
);


// --- MAIN VIEW COMPONENTS ---

// 1. Violation Log Table (Functional Export Implementation)
const ViolationLogTable = () => {
    const [isExporting, setIsExporting] = useState(false);

    // Function to convert data to CSV and trigger download
    const handleExport = () => {
        setIsExporting(true);
        console.log("Starting compliance log export...");

        // Simulate data processing delay (1.5s)
        setTimeout(() => {
            const headers = ["ID", "Time", "Agent", "Violation Type", "Location", "Severity"];
            const csvRows = MOCK_VIOLATION_LOGS.map(log => [
                log.id, 
                log.time, 
                log.agent, 
                log.type, 
                // Escape commas in location field
                `"${log.location.replace(/"/g, '""')}"`, 
                log.severity
            ].join(','));
            
            const csvContent = [
                headers.join(','),
                ...csvRows
            ].join('\n');

            // Trigger the download (functional, though data is mocked)
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `Compliance_Log_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            console.log("Export complete.");
            setIsExporting(false);
        }, 1500);
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-4 flex items-center">
                <ExclamationCircleIcon className="h-6 w-6 mr-2 text-red-600"/>
                Policy Violation Log
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-100 shadow-inner">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-600 uppercase tracking-wider">Time</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-600 uppercase tracking-wider">Agent</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-600 uppercase tracking-wider">Violation Type</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-600 uppercase tracking-wider">Location</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-600 uppercase tracking-wider">Severity</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 font-roboto">
                        {MOCK_VIOLATION_LOGS.map((log) => (
                            <tr key={log.id} className="hover:bg-red-50/50 transition duration-100"> 
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold">{log.time}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{log.agent}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${log.type.includes("Breach") || log.type.includes("Speeding") ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                        {log.type}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{log.location}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-medium">
                                    {/* Enhanced severity pills */}
                                    <span className={`px-2 py-1 text-xs font-bold rounded-full shadow-sm ${log.severity === 'High' || log.severity === 'Critical' ? 'bg-red-700 text-white' : log.severity === 'Medium' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-300 text-gray-700'}`}>
                                        {log.severity}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="mt-4 flex justify-end">
                <ActionButton 
                    onClick={handleExport}
                    className="bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 focus:ring-indigo-500"
                    loading={isExporting}
                >
                    Export Full Log (CSV)
                </ActionButton>
            </div>
        </div>
    );
};

// 2. Exception Approval Queue (Styling and Mobile Refinements)
const ApprovalQueue = () => {
    const [exceptions, setExceptions] = useState(MOCK_ATTENDANCE_EXCEPTIONS);
    const [loadingId, setLoadingId] = useState(null);

    const handleAction = (id, action) => {
        setLoadingId(id);
        
        // Simulate API call delay
        setTimeout(() => {
            // Remove the item after action is completed for real-time update feel
            setExceptions(prev => prev.filter(ex => ex.id !== id)); 
            setLoadingId(null);
        }, 800); 
    };

    const pendingExceptions = useMemo(() => exceptions.filter(ex => ex.status === 'Pending'), [exceptions]);

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 h-full flex flex-col">
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-4 flex items-center">
                <ClipboardDocumentCheckIcon className="h-6 w-6 mr-2 text-purple-600"/>
                Attendance Exception Queue ({pendingExceptions.length} Pending)
            </h3>

            {/* Added min-height for visual stability when the queue is empty */}
            <div className="flex-grow space-y-4 overflow-y-auto min-h-[200px]"> 
                {pendingExceptions.length > 0 ? (
                    pendingExceptions.map((ex) => (
                        <div key={ex.id} className="p-4 border border-indigo-200/50 rounded-lg bg-indigo-50/50 shadow-md flex flex-col md:flex-row justify-between items-start md:items-center transition duration-200 hover:bg-indigo-100/70">
                            <div className="flex-1 min-w-0 mb-3 md:mb-0">
                                <p className="font-semibold text-gray-900 font-poppins">{ex.agent}: <span className="text-indigo-700 font-bold">{ex.type}</span></p>
                                <p className="text-sm text-gray-600 font-roboto line-clamp-2 md:line-clamp-1">Reason: {ex.reason}</p>
                                <div className="mt-2 text-xs font-medium flex items-center">
                                    {/* Enhanced GPS Verification Badges */}
                                    {ex.gpsProof ? (
                                        <div className="flex items-center text-green-700 bg-green-100 px-2 py-0.5 rounded-full shadow-sm">
                                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                                            GPS Verified
                                        </div>
                                    ) : (
                                        <div className="flex items-center text-red-700 bg-red-100 px-2 py-0.5 rounded-full shadow-sm">
                                            <XCircleIcon className="h-4 w-4 mr-1" />
                                            No GPS Proof
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex space-x-2 flex-shrink-0 w-full md:w-auto pt-3 md:pt-0 border-t md:border-t-0 border-indigo-100">
                                <ActionButton
                                    onClick={() => handleAction(ex.id, 'Approve')}
                                    // Added w-1/2 for better mobile touch targets
                                    className="bg-green-600 text-white hover:bg-green-700 active:bg-green-800 focus:ring-green-500 w-1/2 md:w-auto"
                                    loading={loadingId === ex.id}
                                >
                                    Approve
                                </ActionButton>
                                <ActionButton
                                    onClick={() => handleAction(ex.id, 'Reject')}
                                    // Added w-1/2 for better mobile touch targets
                                    className="bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus:ring-red-500 w-1/2 md:w-auto"
                                    loading={loadingId === ex.id}
                                >
                                    Reject
                                </ActionButton>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-10 text-gray-500 font-roboto">
                        <CheckCircleIcon className="h-10 w-10 text-green-400 mx-auto mb-2" />
                        <p className="font-semibold text-lg">Queue Clear</p>
                        <p>All compliance exceptions have been reviewed. Excellent work!</p>
                    </div>
                )}
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500 font-roboto">Note: Approving an exception adjusts the agent's compliance score.</p>
            </div>
        </div>
    );
};


// --- MAIN DASHBOARD COMPONENT ---

export default function RemoteCompliance() {
    return (
        <div 
            className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-10"
            style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
        >
            {/* Page Header */}
            <div className="mb-6 border-b border-gray-200 pb-4">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 font-poppins flex items-center">
                    <ShieldCheckIcon className="h-8 w-8 mr-3 text-indigo-600"/>
                    Remote Compliance Center
                </h1>
                <p className="text-gray-500 mt-1 text-md font-roboto">
                    Audit agent behavior, manage exceptions, and monitor policy adherence based on GPS data.
                </p>
            </div>

            {/* 1. Key Metrics Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <ComplianceSummaryCard 
                    title="Overall Compliance Rate" 
                    value={`${MOCK_COMPLIANCE_METRICS.complianceRate}%`} 
                    icon={CheckCircleIcon} 
                    colorClass="text-green-600" 
                    footer="Target: 95% minimum"
                />
                <ComplianceSummaryCard 
                    title="Policy Violations (Last 7 Days)" 
                    value={MOCK_COMPLIANCE_METRICS.policyViolations} 
                    icon={ExclamationCircleIcon} 
                    colorClass="text-red-600" 
                    footer="Includes Speeding & Geo-fences"
                />
                <ComplianceSummaryCard 
                    title="Pending Exceptions" 
                    value={MOCK_COMPLIANCE_METRICS.pendingApprovals} 
                    icon={ClipboardDocumentCheckIcon} 
                    colorClass="text-yellow-600" 
                    footer="Urgent review required"
                />
                <ComplianceSummaryCard 
                    title="On-Time Check-Ins" 
                    value={MOCK_COMPLIANCE_METRICS.onTimeCheckIns} 
                    icon={ClockIcon} 
                    colorClass="text-blue-600" 
                    footer="Total agents: 152"
                />
            </div>

            {/* 2. Log and Approval Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                
                {/* Policy Violation Log (3/5 width) */}
                <div className="lg:col-span-3">
                    <ViolationLogTable />
                </div>

                {/* Exception Approval Queue (2/5 width) */}
                <div className="lg:col-span-2">
                    <ApprovalQueue />
                </div>
            </div>
            
            {/* 3. Compliance Settings (Mock Footer) */}
            <div className="mt-8 pt-4 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-500 font-roboto">
                    Compliance policies are configured in the <a href="#" className="text-indigo-600 hover:text-indigo-700 font-semibold">Settings Portal</a>. Last updated: {new Date().toLocaleDateString()}.
                </p>
            </div>

        </div>
    );
}